import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaArrowLeft,
  FaChartLine,
  FaChartBar,
  FaLightbulb,
  FaSpinner,
  FaTrophy,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import axios from 'axios';
import api from '../../api';
import { useAuth } from '../../context/AuthContext';
import Loader from '../../components/Loader/Loader';
import './AnalyticsPage.css';

const AnalyticsPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [aiFeedback, setAiFeedback] = useState(null);
  const [loadingFeedback, setLoadingFeedback] = useState(false);

  const fetchAnalytics = useCallback(async () => {
    try {
      const res = await axios.get(`${api.baseURL}/api/exams/analytics/performance`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) setAnalytics(res.data.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const fetchAIFeedback = async () => {
    setLoadingFeedback(true);
    try {
      const res = await axios.get(`${api.baseURL}/api/exams/analytics/ai-feedback`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) setAiFeedback(res.data.data);
    } catch (error) {
      console.error('Error fetching AI feedback:', error);
      toast.error('Failed to load AI feedback');
    } finally {
      setLoadingFeedback(false);
    }
  };

  if (loading) return <Loader message="Loading analytics..." />;

  return (
    <div className="analytics-page">
      <header className="analytics-header">
        <button className="analytics-back-btn" onClick={() => navigate('/university/dashboard')}>
          <FaArrowLeft /> Back
        </button>
        <h1>Performance Analytics</h1>
        <div></div>
      </header>

      {/* Overview Cards */}
      <section className="analytics-overview">
        <div className="analytics-overview-card">
          <span className="analytics-overview-number">{analytics?.totalExamsTaken || 0}</span>
          <span className="analytics-overview-label">Total Exams</span>
        </div>
        <div className="analytics-overview-card">
          <span className="analytics-overview-number">{(analytics?.overallAccuracy || 0).toFixed(1)}%</span>
          <span className="analytics-overview-label">Overall Accuracy</span>
        </div>
        <div className="analytics-overview-card">
          <span className="analytics-overview-number">{analytics?.certificatesEarned || 0}</span>
          <span className="analytics-overview-label">Certificates</span>
        </div>
        <div className="analytics-overview-card">
          <span className="analytics-overview-number">{analytics?.subjectPerformance?.length || 0}</span>
          <span className="analytics-overview-label">Subjects Attempted</span>
        </div>
      </section>

      {/* Subject Performance */}
      {analytics?.subjectPerformance?.length > 0 && (
        <section className="analytics-subjects">
          <h2><FaChartBar /> Subject-wise Performance</h2>
          <div className="analytics-subject-list">
            {analytics.subjectPerformance.map((sp, i) => (
              <div key={i} className="analytics-subject-card">
                <div className="analytics-subject-header">
                  <h3>{sp.subject}</h3>
                  <span className="analytics-subject-category">{sp.category} → {sp.subcategory}</span>
                </div>
                <div className="analytics-bar-wrapper">
                  <div className="analytics-bar-bg">
                    <div
                      className="analytics-bar-fill"
                      style={{ width: `${Math.min(sp.averageScore, 100)}%` }}
                    ></div>
                  </div>
                  <span className="analytics-bar-label">{sp.averageScore.toFixed(1)}%</span>
                </div>
                <div className="analytics-subject-meta">
                  <span>Attempts: {sp.totalAttempts}</span>
                  <span>Best: {sp.highestScore.toFixed(1)}%</span>
                  <span>Last: {sp.lastAttemptAt ? new Date(sp.lastAttemptAt).toLocaleDateString() : 'N/A'}</span>
                </div>
                {sp.weakTopics?.length > 0 && (
                  <div className="analytics-topics">
                    <span className="analytics-topics-label weak">Weak:</span>
                    {sp.weakTopics.map((t, j) => (
                      <span key={j} className="analytics-topic-tag weak">{t}</span>
                    ))}
                  </div>
                )}
                {sp.strongTopics?.length > 0 && (
                  <div className="analytics-topics">
                    <span className="analytics-topics-label strong">Strong:</span>
                    {sp.strongTopics.map((t, j) => (
                      <span key={j} className="analytics-topic-tag strong">{t}</span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Recent Exam Trend */}
      {analytics?.recentExams?.length > 0 && (
        <section className="analytics-trend">
          <h2><FaChartLine /> Recent Exam Trend</h2>
          <div className="analytics-trend-chart">
            {analytics.recentExams.slice(0, 10).map((exam, i) => (
              <div key={i} className="analytics-trend-bar-wrapper">
                <div className="analytics-trend-bar-container">
                  <div
                    className={`analytics-trend-bar ${exam.percentage >= 45 ? 'pass' : 'fail'}`}
                    style={{ height: `${Math.max(exam.percentage, 5)}%` }}
                  ></div>
                </div>
                <span className="analytics-trend-label" title={exam.subject}>
                  {exam.subject.substring(0, 8)}
                </span>
                <span className="analytics-trend-score">{exam.percentage?.toFixed(0)}%</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Global Areas */}
      <section className="analytics-global-areas">
        {analytics?.strongAreasGlobal?.length > 0 && (
          <div className="analytics-global-card strong">
            <h3><FaTrophy /> Your Strengths</h3>
            <div className="analytics-tags">
              {analytics.strongAreasGlobal.map((area, i) => (
                <span key={i} className="analytics-global-tag strong">{area}</span>
              ))}
            </div>
          </div>
        )}
        {analytics?.weakAreasGlobal?.length > 0 && (
          <div className="analytics-global-card weak">
            <h3><FaLightbulb /> Areas to Improve</h3>
            <div className="analytics-tags">
              {analytics.weakAreasGlobal.map((area, i) => (
                <span key={i} className="analytics-global-tag weak">{area}</span>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* AI Feedback */}
      <section className="analytics-ai-section">
        {!aiFeedback ? (
          <button className="analytics-ai-btn" onClick={fetchAIFeedback} disabled={loadingFeedback}>
            {loadingFeedback ? (
              <><FaSpinner className="spinning" /> Generating AI Insights...</>
            ) : (
              <><FaLightbulb /> Get AI Performance Insights</>
            )}
          </button>
        ) : (
          <div className="analytics-ai-card">
            <h3><FaLightbulb /> AI Performance Insights</h3>
            <p className="analytics-ai-assessment">{aiFeedback.overallAssessment}</p>

            {aiFeedback.studyRecommendations?.length > 0 && (
              <div className="analytics-ai-group">
                <h4>Study Recommendations</h4>
                <ul>
                  {aiFeedback.studyRecommendations.map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              </div>
            )}

            {aiFeedback.nextSteps?.length > 0 && (
              <div className="analytics-ai-group">
                <h4>Next Steps</h4>
                <ul>
                  {aiFeedback.nextSteps.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </div>
            )}

            {aiFeedback.motivationalMessage && (
              <div className="analytics-ai-motivation">
                {aiFeedback.motivationalMessage}
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
};

export default AnalyticsPage;
